import { useState } from 'react'
import { Button, Group, Modal, Stack, TagsInput, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { emailOrgTree } from '@/api/finopsOrgTree'

interface Props {
  opened: boolean
  onClose: () => void
  ym: string
  source: 'snapshot' | 'athena'
}

/**
 * Regex liberal de email — não pretende ser RFC-5322 completo,
 * só barrar erros óbvios (sem @ ou sem TLD) antes de chamar a API.
 * Backend (`emailOrgTree`) e SES validam de novo (defense in depth).
 */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/**
 * Modal pra envio do relatório Org Tree por email (anexo PDF gerado server-side).
 *
 * Validações client-side:
 * - 1..20 destinatários
 * - Cada email passa em `EMAIL_RE`
 * - Subject máx 200 chars (Mantine TextInput maxLength já barra digitação)
 *
 * Em erro de validação, exibe notification vermelha e não chama API.
 * Em sucesso, chama `notifications.show` verde e fecha o modal.
 */
export function EmailExportModal({ opened, onClose, ym, source }: Props) {
  const [recipients, setRecipients] = useState<string[]>([])
  const [subject, setSubject] = useState<string>(
    `Relatório por Organização — ${ym}`,
  )
  const [loading, setLoading] = useState(false)

  const submit = async () => {
    if (recipients.length === 0) {
      notifications.show({
        color: 'red',
        message: 'Adicione pelo menos um destinatário',
      })
      return
    }
    if (recipients.length > 20) {
      notifications.show({ color: 'red', message: 'Máximo de 20 destinatários' })
      return
    }
    const bad = recipients.find((r) => !EMAIL_RE.test(r))
    if (bad) {
      notifications.show({ color: 'red', message: `E-mail inválido: ${bad}` })
      return
    }
    setLoading(true)
    try {
      await emailOrgTree({ ym, source, recipients, subject })
      notifications.show({ color: 'green', message: 'E-mail enviado!' })
      onClose()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      notifications.show({ color: 'red', message: `Erro: ${msg}` })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Enviar relatório por e-mail">
      <Stack>
        <TagsInput
          label="Destinatários"
          placeholder="Digite emails (Enter ou vírgula)"
          value={recipients}
          onChange={setRecipients}
          maxTags={20}
          splitChars={[',', ' ', ';']}
        />
        <TextInput
          label="Assunto"
          value={subject}
          onChange={(e) => setSubject(e.currentTarget.value)}
          maxLength={200}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={loading}>
            Enviar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
